import { useEffect, useState, useCallback } from 'react';
import { fetchB2CAuditLog } from '@/lib/api';
import { usePlatformStore } from '@/lib/store';
import { Loader2, ChevronDown, Filter } from 'lucide-react';
import { cn } from '@/utils/helpers';
import type { B2CAuditEntry } from '@/types';

const EVENT_COLORS: Record<string, string> = {
  plan_features_updated: 'badge-blue',
  user_override_created: 'badge-emerald',
  user_override_deleted: 'badge-red',
  pricing_updated: 'badge-amber',
};

const PAGE_SIZE = 25;

export function AuditLogTab() {
  const { tenants } = usePlatformStore();
  const [entries, setEntries] = useState<B2CAuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [tenantFilter, setTenantFilter] = useState<string>('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchB2CAuditLog(
        tenantFilter || undefined,
        PAGE_SIZE,
        offset,
      );
      setEntries(result.entries);
      setTotal(result.total);
    } catch (err) {
      console.error('Failed to load audit log:', err);
    }
    setLoading(false);
  }, [tenantFilter, offset]);

  useEffect(() => { load(); }, [load]);

  const handleFilterChange = (tenantId: string) => {
    setTenantFilter(tenantId);
    setOffset(0);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  const tenantMap = new Map(tenants.map(t => [t.id, t.name]));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-surface-500">
          Audit trail of all feature gating and pricing changes across the B2C platform.
        </p>

        <div className="flex items-center gap-2">
          <Filter size={14} className="text-surface-500" />
          <select value={tenantFilter} onChange={e => handleFilterChange(e.target.value)}
            className="input-field py-1.5 text-xs w-48">
            <option value="">All Tenants</option>
            {tenants.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="animate-spin text-brand-600 dark:text-brand-400" size={24} />
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-12 text-sm text-surface-500">
          No audit log entries found.
        </div>
      ) : (
        <div className="glass-panel overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-surface-200">
                <th className="table-header-cell w-40">Timestamp</th>
                <th className="table-header-cell w-36">Event</th>
                <th className="table-header-cell w-32">Actor</th>
                <th className="table-header-cell w-28">Type</th>
                <th className="table-header-cell w-36">Tenant</th>
                <th className="table-header-cell w-10"></th>
              </tr>
            </thead>
            <tbody>
              {entries.map(entry => {
                const expanded = expandedId === entry.id;
                return (
                  <AuditRow key={entry.id} entry={entry} expanded={expanded}
                    tenantName={entry.tenant_id ? tenantMap.get(entry.tenant_id) || entry.tenant_id.slice(0, 8) : '—'}
                    onToggle={() => setExpandedId(expanded ? null : entry.id)} />
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-surface-500">
            Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </span>
          <div className="flex gap-1">
            <button onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={offset === 0}
              className="px-3 py-1 text-xs border border-surface-200 rounded-lg disabled:opacity-50 hover:bg-surface-50 transition-colors">
              Previous
            </button>
            <span className="px-3 py-1 text-xs text-surface-500">
              Page {currentPage} of {totalPages}
            </span>
            <button onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={offset + PAGE_SIZE >= total}
              className="px-3 py-1 text-xs border border-surface-200 rounded-lg disabled:opacity-50 hover:bg-surface-50 transition-colors">
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AuditRow({ entry, expanded, tenantName, onToggle }: {
  entry: B2CAuditEntry;
  expanded: boolean;
  tenantName: string;
  onToggle: () => void;
}) {
  const eventColor = EVENT_COLORS[entry.event_type] || 'bg-surface-100 text-surface-500';
  const ts = new Date(entry.created_at);

  return (
    <>
      <tr className="table-row cursor-pointer" onClick={onToggle}>
        <td className="table-cell">
          <div className="text-xs text-surface-700">{ts.toLocaleDateString()}</div>
          <div className="text-[10px] text-surface-500">{ts.toLocaleTimeString()}</div>
        </td>
        <td className="table-cell">
          <span className={cn('text-[10px] px-1.5 py-0.5 rounded font-medium', eventColor)}>
            {entry.event_type.replace(/_/g, ' ')}
          </span>
        </td>
        <td className="table-cell">
          <div className="text-xs text-surface-700">{entry.actor_email || entry.actor_id}</div>
        </td>
        <td className="table-cell">
          <span className={cn('text-[10px] px-1.5 py-0.5 rounded font-medium',
            entry.actor_type === 'super_admin' ? 'badge-purple' : 'bg-surface-100 text-surface-500')}>
            {entry.actor_type === 'super_admin' ? 'Super Admin' : 'Tenant Admin'}
          </span>
        </td>
        <td className="table-cell">
          <span className="text-xs text-surface-600">{tenantName}</span>
        </td>
        <td className="table-cell">
          <ChevronDown className={cn('w-3 h-3 text-surface-400 transition-transform', expanded && 'rotate-180')} />
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} className="px-5 py-3 bg-surface-50 border-b border-surface-200">
            <div className="space-y-2">
              <div className="flex gap-4 text-[10px]">
                <span className="text-surface-500">Resource: <span className="text-surface-700">{entry.resource_type}</span></span>
                {entry.resource_id && (
                  <span className="text-surface-500">ID: <span className="text-surface-700 font-mono">{entry.resource_id}</span></span>
                )}
              </div>
              {entry.payload && Object.keys(entry.payload).length > 0 && (
                <pre className="text-[10px] text-surface-600 bg-surface-100 rounded-lg p-3 overflow-x-auto font-mono leading-relaxed">
                  {JSON.stringify(entry.payload, null, 2)}
                </pre>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
